import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Link, Paper, Snackbar,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { buscarClientes, getCatalogoRegistro, registrarEquipo } from "../services/registroEquiposService";

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 12.5 },
  "& .MuiOutlinedInput-root": { fontSize: 13 },
};

// Empresas tienen razón social; las personas, nombres y apellidos.
const nombreCliente = (c) => {
  const persona = [c.primer_nombre, c.segundo_nombre, c.apellido_paterno, c.apellido_materno].filter(Boolean).join(" ");
  return `${(c.razon_social || "").trim() || persona || c.cod_cliente} (${c.cod_cliente})`;
};

const nombreModelo = (m) => (m?.nombre || "").trim() || "(sin modelo)";

// Formulario único para radio, módem, chip, batería y equipos varios. El tipo de modelo y el modelo salen del catálogo
// (pantalla "Modelos de equipos"); el resto de campos cambia según `cfg`:
//   cfg.clave   = identificador único del equipo; cfg.extras = otros campos; cfg.cliente = módem que puede ser del cliente.
export default function RegistroEquipoForm({ cfg }) {
  const { tipo, color = "primary", icono: Icono } = cfg;
  const [cat, setCat] = useState(null);
  const [errorCat, setErrorCat] = useState("");
  const [v, setV] = useState({});
  const [clienteSel, setClienteSel] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [textoCliente, setTextoCliente] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [toast, setToast] = useState(null);
  const [recientes, setRecientes] = useState([]);
  const claveRef = useRef(null);
  const temporizador = useRef(null);

  const set = (name, value) => setV((prev) => ({ ...prev, [name]: value }));
  const avisar = useCallback((severity, texto, ms) => {
    clearTimeout(temporizador.current);
    setToast({ severity, texto });
    temporizador.current = setTimeout(() => setToast(null), ms);
  }, []);
  useEffect(() => () => clearTimeout(temporizador.current), []);

  useEffect(() => {
    let vivo = true;
    getCatalogoRegistro(tipo)
      .then(({ data }) => {
        if (!vivo) return;
        setCat(data);
        setV((prev) => ({ ...prev, ciudad: data.ciudad_fija || prev.ciudad || "", ...(cfg.cliente ? { propio: false } : {}) }));
      })
      .catch((e) => vivo && setErrorCat(e?.response?.data?.error || "No se pudo cargar el catálogo de modelos."));
    return () => { vivo = false; };
  }, [tipo, cfg.cliente]);

  // Búsqueda de cliente (solo para módems que pertenecen al cliente).
  useEffect(() => {
    if (!v.propio || textoCliente.trim().length < 2) { setClientes([]); return undefined; }
    const t = setTimeout(() => {
      buscarClientes(textoCliente.trim()).then(({ data }) => setClientes(data?.rows || [])).catch(() => setClientes([]));
    }, 300);
    return () => clearTimeout(t);
  }, [textoCliente, v.propio]);

  const tipoSel = useMemo(() => cat?.tipos.find((t) => t.id === v.id_tipo_modelo) || null, [cat, v.id_tipo_modelo]);
  const modelosDelTipo = useMemo(() => (cat ? cat.modelos.filter((m) => m.id_tipo === v.id_tipo_modelo) : []), [cat, v.id_tipo_modelo]);
  const modeloSel = modelosDelTipo.find((m) => m.id === v.id_modelo) || null;

  const elegirTipo = (t) => {
    const modelos = t && cat ? cat.modelos.filter((m) => m.id_tipo === t.id) : [];
    // Si el tipo de modelo tiene un solo modelo, queda elegido para ahorrar un paso.
    setV((prev) => ({ ...prev, id_tipo_modelo: t?.id ?? null, id_modelo: modelos.length === 1 ? modelos[0].id : null }));
  };

  const campos = [cfg.clave, ...(cfg.extras || [])];
  const claveValor = String(v[cfg.clave.name] || "").trim();
  const faltan = [];
  if (!claveValor) faltan.push(cfg.clave.label);
  if (!v.id_tipo_modelo) faltan.push("Tipo de modelo");
  if (!v.id_modelo) faltan.push("Modelo");
  if (!String(v.marca || "").trim()) faltan.push("Marca");
  if (!v.ciudad) faltan.push("Ciudad");
  (cfg.extras || []).forEach((f) => { if (f.required && !String(v[f.name] || "").trim()) faltan.push(f.label); });
  if (cfg.cliente && v.propio && !clienteSel) faltan.push("Cliente");
  const errorClave = tipo === "radio" && claveValor.includes("/") ? "La serie no puede tener el carácter /" : "";

  const puedeGuardar = faltan.length === 0 && !errorClave && !guardando;

  const guardar = async () => {
    setConfirmando(false);
    setGuardando(true);
    try {
      const body = {
        [cfg.clave.name]: claveValor, id_tipo_modelo: v.id_tipo_modelo, id_modelo: v.id_modelo, marca: String(v.marca).trim(), ciudad: v.ciudad, observacion: v.observacion || "",
      };
      (cfg.extras || []).forEach((f) => { body[f.name] = v[f.name] || ""; });
      if (cfg.cliente) { body.propio = v.propio ? 1 : 0; if (v.propio) body.cod_cliente = clienteSel.cod_cliente; }
      const { data } = await registrarEquipo(tipo, body);
      avisar("success", `${cfg.titulo}: ${claveValor} registrado correctamente.`, 3500);
      setRecientes((prev) => [{ clave: claveValor, modelo: data.modelo, ciudad: data.ciudad, hora: new Date().toLocaleTimeString("es-BO", { hour: "2-digit", minute: "2-digit" }) }, ...prev].slice(0, 8));
      // Para registrar varios seguidos se conserva todo lo común y solo se limpia lo propio de cada equipo.
      setV((prev) => {
        const limpio = { ...prev, observacion: "" };
        campos.forEach((f) => { if (!f.persistir) limpio[f.name] = ""; });
        return limpio;
      });
      setTimeout(() => claveRef.current?.focus(), 50);
    } catch (e) {
      avisar("error", e?.response?.data?.error || e?.message || "No se pudo registrar el equipo.", 6000);
    } finally {
      setGuardando(false);
    }
  };

  const limpiarTodo = () => {
    setV({ ciudad: cat?.ciudad_fija || "", ...(cfg.cliente ? { propio: false } : {}) });
    setClienteSel(null);
    setTextoCliente("");
  };

  if (errorCat) return <Alert severity="error">{errorCat}</Alert>;
  if (!cat) return <Box sx={{ p: 3, display: "grid", placeItems: "center" }}><CircularProgress size={28} /></Box>;

  const sinCatalogo = cat.tipos.length === 0;

  return (
    <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 760px) minmax(0, 1fr)" }, alignItems: "start" }}>
      <Paper variant="outlined" sx={{ overflow: "hidden", borderColor: (t) => alpha(t.palette[color].main, 0.4) }}>
        <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1, bgcolor: (t) => alpha(t.palette[color].main, 0.12), borderBottom: 1, borderColor: (t) => alpha(t.palette[color].main, 0.4) }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: `${color}.main`, color: `${color}.contrastText` }}>
            {Icono && <Icono fontSize="small" />}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, color: `${color}.main` }}>Registrar {cfg.titulo.toLowerCase()}</Typography>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Entra a {cat.estado_inicial === "Almacen" ? "almacén" : cat.estado_inicial} y queda anotado en su historial.</Typography>
          </Box>
        </Box>

        <Box sx={{ p: 1.5 }}>
          {sinCatalogo && <Alert severity="warning" sx={{ mb: 1.25 }}>No hay tipos de modelo para este equipo. Créalos primero en <Link component={RouterLink} to="/modelos-equipos">Modelos de equipos</Link>.</Alert>}

          <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary", mb: 0.75 }}>1. ¿QUÉ EQUIPO ES?</Typography>
          <Grid container spacing={1.25}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small" options={cat.tipos} value={tipoSel} onChange={(_, t) => elegirTipo(t)} getOptionLabel={(t) => t.nombre.trim()} isOptionEqualToValue={(a, b) => a.id === b.id}
                noOptionsText="Sin resultados" sx={controlSx} renderInput={(p) => <TextField {...p} label="Tipo de modelo" required />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small" options={modelosDelTipo} value={modeloSel} onChange={(_, m) => set("id_modelo", m?.id ?? null)} getOptionLabel={nombreModelo} isOptionEqualToValue={(a, b) => a.id === b.id}
                disabled={!tipoSel} noOptionsText="Este tipo de modelo no tiene modelos" sx={controlSx} renderInput={(p) => <TextField {...p} label="Modelo" required />}
              />
            </Grid>
          </Grid>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5, mb: 1.5 }}>
            ¿No encuentras el modelo? Regístralo en <Link component={RouterLink} to="/modelos-equipos">Modelos de equipos</Link>.
          </Typography>

          <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary", mb: 0.75 }}>2. IDENTIFICACIÓN</Typography>
          {cfg.cliente && (
            <Box sx={{ mb: 1 }}>
              <FormControlLabel
                control={<Checkbox size="small" checked={!!v.propio} onChange={(e) => { set("propio", e.target.checked); if (!e.target.checked) setClienteSel(null); }} />}
                label={<Typography sx={{ fontSize: 13 }}>El equipo es del cliente (no de TRS)</Typography>}
              />
              {v.propio && (
                <Autocomplete
                  size="small" options={clientes} value={clienteSel} onChange={(_, c) => setClienteSel(c)} inputValue={textoCliente} onInputChange={(_, t) => setTextoCliente(t)}
                  getOptionLabel={nombreCliente} isOptionEqualToValue={(a, b) => a.cod_cliente === b.cod_cliente}
                  filterOptions={(x) => x} noOptionsText={textoCliente.trim().length < 2 ? "Escribe al menos 2 letras" : "Sin resultados"} sx={controlSx}
                  renderInput={(p) => <TextField {...p} label="Cliente" required />}
                />
              )}
            </Box>
          )}
          <Grid container spacing={1.25}>
            {campos.map((f, i) => (
              <Grid key={f.name} size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth size="small" label={f.label} required={i === 0 || !!f.required} value={v[f.name] || ""} onChange={(e) => set(f.name, e.target.value)}
                  inputRef={i === 0 ? claveRef : undefined} autoFocus={i === 0} type={f.type === "date" ? "date" : "text"} slotProps={{ htmlInput: { maxLength: f.maxLength }, inputLabel: f.type === "date" ? { shrink: true } : undefined }}
                  error={i === 0 && !!errorClave} helperText={i === 0 ? errorClave || f.ayuda : f.ayuda} sx={controlSx}
                />
              </Grid>
            ))}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo size="small" options={cat.marcas} inputValue={v.marca || ""} onInputChange={(_, t) => set("marca", t)} sx={controlSx}
                renderInput={(p) => <TextField {...p} label="Marca" required />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small" options={cat.ciudades} value={v.ciudad || null} onChange={(_, c) => set("ciudad", c || "")} disabled={!!cat.ciudad_fija} disableClearable sx={controlSx}
                renderInput={(p) => <TextField {...p} label="Ciudad" required helperText={cat.ciudad_fija ? "Tu sucursal" : undefined} />}
              />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Observación" multiline minRows={2} value={v.observacion || ""} onChange={(e) => set("observacion", e.target.value)} slotProps={{ htmlInput: { maxLength: 150 } }} sx={controlSx} />
            </Grid>
          </Grid>

          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", flex: 1, minWidth: 180 }}>
              {faltan.length ? `Falta: ${faltan.join(", ")}` : "Todo listo para registrar."}
            </Typography>
            <Button onClick={limpiarTodo} disabled={guardando}>Limpiar</Button>
            <Button variant="contained" color={color} disabled={!puedeGuardar} onClick={() => setConfirmando(true)} startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}>
              Registrar
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box sx={{ px: 1.5, py: 0.75, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800 }}>REGISTRADOS AHORA</Typography>
          <Typography sx={{ fontSize: 11, color: "text.secondary" }}>Lo que registraste en esta sesión</Typography>
        </Box>
        {recientes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>Todavía no registraste ninguno. El tipo de modelo, modelo, marca y ciudad se mantienen para que registres el siguiente más rápido.</Typography>
        ) : (
          <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.4, px: 1, fontSize: 12 } }}>
            <TableHead><TableRow><TableCell>{cfg.clave.label}</TableCell><TableCell>Modelo</TableCell><TableCell>Ciudad</TableCell><TableCell>Hora</TableCell></TableRow></TableHead>
            <TableBody>
              {recientes.map((r, i) => <TableRow key={`${r.clave}-${i}`}><TableCell sx={{ fontWeight: 700 }}>{r.clave}</TableCell><TableCell>{r.modelo}</TableCell><TableCell>{r.ciudad}</TableCell><TableCell>{r.hora}</TableCell></TableRow>)}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={confirmando} onClose={() => setConfirmando(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 800 }}>Registrar {cfg.titulo.toLowerCase()}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, mb: 1 }}>¿Registrar este equipo?</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.25, fontSize: 13.5 }}>
            <b>{cfg.clave.label}:</b><span>{claveValor}</span>
            <b>Modelo:</b><span>{tipoSel?.nombre.trim()} {modeloSel?.nombre?.trim()}</span>
            <b>Marca:</b><span>{v.marca}</span>
            <b>Ciudad:</b><span>{v.ciudad}</span>
            {cfg.cliente && v.propio && clienteSel && (<><b>Del cliente:</b><span>{nombreCliente(clienteSel)}</span></>)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmando(false)}>Cancelar</Button>
          <Button variant="contained" color={color} autoFocus onClick={guardar}>Sí, registrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={toast?.severity || "success"} variant="filled" sx={{ fontWeight: 600 }}>{toast?.texto}</Alert>
      </Snackbar>
    </Box>
  );
}
